import type { AppEvent, CompletionLevel } from './events.js';
import type { BehaviourWindow, Ruleset, TitleId } from './ruleset.js';
import { rankProgress } from './ruleset.js';
import { expectedWeight } from './schedule.js';
import type { DayStats, DerivedState, Habit } from './state.js';
import { applyOrder, completionKey, emptyDay, initialState } from './state.js';
import { dayIndex, localHour } from './time.js';
import type { StudyDate } from './time.js';

/** Rolling window Titles are scored over. Short enough that a title actually
 *  changes as behaviour changes — the whole point of §10. */

/**
 * Completion hours kept per habit.
 *
 * Enough to see a rhythm, few enough that a habit which has MOVED — mornings
 * to evenings — proposes where it lives now rather than where it started.
 */
export const RHYTHM_SAMPLES = 8;
export const TITLE_WINDOW_DAYS = 14;

const ACTIVITY_TYPES = new Set<AppEvent['type']>([
  'habit.completed',
  'task.completed',
  'entry.logged',
]);

/** Containers are shallow-cloned so `reduce` is pure to callers while the
 *  fold mutates freely inside. O(entities), not O(events). */
function cloneState(state: DerivedState): DerivedState {
  return {
    ...state,
    habits: { ...state.habits },
    habitOrder: [...state.habitOrder],
    tasks: { ...state.tasks },
    goals: { ...state.goals },
    entries: { ...state.entries },
    measures: { ...state.measures },
    readings: { ...state.readings },
    completions: { ...state.completions },
    days: { ...state.days },
    tracks: { ...state.tracks },
    totals: { ...state.totals },
    achievements: { ...state.achievements },
    ranksReached: { ...state.ranksReached },
    streak: { ...state.streak },
  };
}

function touchDay(draft: DerivedState, date: StudyDate): DayStats {
  const existing = draft.days[date];
  const day = existing ? { ...existing } : emptyDay(date);
  draft.days[date] = day;
  return day;
}

/**
 * Minute XP is computed from the day's TOTAL minutes, not per log entry.
 *
 * Per-entry would be trivially gameable: splitting one 4-hour session into
 * eight logs would pay the lucrative first-hour rate eight times. Applying
 * the diminishing curve to the daily total closes that, and is a more
 * honest model of the day anyway.
 */
function recomputeMinuteXp(day: DayStats, ruleset: Ruleset): void {
  day.xpMinutes = Math.min(ruleset.xp.dailyMinutesCap, ruleset.xp.minutes(day.minutes));
}

/**
 * What one kept promise is worth at a given level.
 *
 * Rounded, so the floor is a whole number of points rather than something the
 * user sees as 3.0000000000000004 in a share card one day.
 */
function habitAward(ruleset: Ruleset, level: CompletionLevel): number {
  const full = ruleset.xp.flat['habit.completed'] ?? 0;
  return level === 'floor' ? Math.round(full * ruleset.xp.floorRatio) : full;
}

function addTrack(draft: DerivedState, track: string, minutes: number, sessions: number): void {
  const current = draft.tracks[track] ?? { minutes: 0, timedSessions: 0 };
  draft.tracks[track] = {
    minutes: Math.max(0, current.minutes + minutes),
    timedSessions: Math.max(0, current.timedSessions + sessions),
  };
}

function applyEvent(draft: DerivedState, event: AppEvent, ruleset: Ruleset): void {
  const day = touchDay(draft, event.studyDate);

  if (ACTIVITY_TYPES.has(event.type)) {
    day.active = true;
    day.activities += 1;
    const hour = localHour(event.ts, event.tzOffsetMinutes);
    if (hour >= 22 || hour < 4) day.lateActivities += 1;
    else if (hour < 8) day.earlyActivities += 1;
  }

  const flat = ruleset.xp.flat[event.type];
  if (flat) day.xpFlat += flat;
  // Not keyed off the event type: a typed hour and a timed hour are the same
  // event now, and it is committing to the block in advance that earns this.
  if (event.type === 'entry.logged' && event.mode === 'timed') {
    day.xpFlat += ruleset.xp.timedSessionBonus;
  }

  switch (event.type) {
    // ── definitions ──────────────────────────────────────────────────────
    case 'habit.created':
      draft.habits[event.habitId] = {
        id: event.habitId,
        name: event.name,
        schedule: event.schedule,
        archived: false,
        createdOn: event.studyDate,
        recentHours: [],
        ...(event.icon === undefined ? {} : { icon: event.icon }),
        ...(event.floor === undefined ? {} : { floor: event.floor }),
        ...(event.anchor === undefined ? {} : { anchor: event.anchor }),
        ...(event.place === undefined ? {} : { place: event.place }),
      };
      draft.habitOrder = [...draft.habitOrder, event.habitId];
      break;
    case 'habit.renamed': {
      const habit = draft.habits[event.habitId];
      if (habit) draft.habits[event.habitId] = { ...habit, name: event.name };
      break;
    }
    case 'habit.floorSet': {
      const habit = draft.habits[event.habitId];
      if (habit) {
        // An empty string clears it, so a student who wrote a floor they
        // dislike is not stuck with it forever.
        const floor = event.floor.trim();
        const { floor: _dropped, ...rest } = habit;
        draft.habits[event.habitId] = floor ? { ...habit, floor } : rest;
      }
      break;
    }
    case 'habit.planSet': {
      const habit = draft.habits[event.habitId];
      if (habit) {
        const anchor = event.anchor.trim();
        const place = event.place.trim();
        const { anchor: _a, place: _p, ...rest } = habit;
        draft.habits[event.habitId] = {
          ...rest,
          ...(anchor ? { anchor } : {}),
          ...(place ? { place } : {}),
        };
      }
      break;
    }
    case 'habit.rescheduled': {
      const habit = draft.habits[event.habitId];
      if (habit) draft.habits[event.habitId] = { ...habit, schedule: event.schedule };
      break;
    }
    case 'habit.archived': {
      const habit = draft.habits[event.habitId];
      if (habit) draft.habits[event.habitId] = { ...habit, archived: true };
      break;
    }

    case 'habit.reordered':
      draft.habitOrder = applyOrder(draft.habitOrder, event.habitIds);
      break;

    case 'task.created':
      draft.tasks[event.taskId] = {
        id: event.taskId,
        name: event.name,
        done: false,
        archived: false,
        createdOn: event.studyDate,
        ...(event.track === undefined ? {} : { track: event.track }),
        ...(event.dueDate === undefined ? {} : { dueDate: event.dueDate }),
      };
      day.tasksOpened += 1;
      break;
    case 'task.edited': {
      const task = draft.tasks[event.taskId];
      if (task) {
        draft.tasks[event.taskId] = {
          ...task,
          ...(event.name === undefined ? {} : { name: event.name }),
          ...(event.track === undefined ? {} : { track: event.track }),
          ...(event.dueDate === undefined ? {} : { dueDate: event.dueDate }),
        };
      }
      break;
    }
    case 'task.archived': {
      const task = draft.tasks[event.taskId];
      if (task) draft.tasks[event.taskId] = { ...task, archived: true };
      break;
    }

    case 'goal.started':
      draft.goals[event.goalId] = {
        id: event.goalId,
        preset: event.preset,
        title: event.title,
        category: event.category,
        targetDate: event.targetDate,
        tracks: event.tracks,
        startedOn: event.studyDate,
        parts: event.parts ?? [],
        partsCompleted: [],
        status: 'active',
        // `exactOptionalPropertyTypes`: an absent field must not be written
        // as `undefined`, or it stops being absent.
        ...(event.outcome === undefined ? {} : { outcome: event.outcome }),
        ...(event.obstacle === undefined ? {} : { obstacle: event.obstacle }),
        ...(event.doneWhen === undefined ? {} : { doneWhen: event.doneWhen }),
      };
      draft.totals.goalsStarted += 1;
      break;
    case 'goal.edited': {
      const goal = draft.goals[event.goalId];
      // Absent means unchanged, never cleared — see the event's own note.
      if (goal) {
        draft.goals[event.goalId] = {
          ...goal,
          ...(event.title === undefined ? {} : { title: event.title }),
          ...(event.outcome === undefined ? {} : { outcome: event.outcome }),
          ...(event.obstacle === undefined ? {} : { obstacle: event.obstacle }),
          ...(event.doneWhen === undefined ? {} : { doneWhen: event.doneWhen }),
          ...(event.targetDate === undefined ? {} : { targetDate: event.targetDate }),
        };
      }
      break;
    }
    case 'goal.partsSet': {
      const goal = draft.goals[event.goalId];
      if (goal) {
        // Completions for parts that no longer exist are dropped rather than
        // kept as ghosts: progress is `partsCompleted` over `parts`, and a
        // stale entry would push the ratio above one.
        const parts = new Set(event.parts);
        draft.goals[event.goalId] = {
          ...goal,
          parts: event.parts,
          partsCompleted: goal.partsCompleted.filter((part) => parts.has(part)),
        };
      }
      break;
    }
    case 'goal.partCompleted': {
      const goal = draft.goals[event.goalId];
      if (goal && goal.parts.includes(event.part) && !goal.partsCompleted.includes(event.part)) {
        draft.goals[event.goalId] = {
          ...goal,
          partsCompleted: [...goal.partsCompleted, event.part],
        };
      }
      break;
    }
    case 'goal.ended': {
      const goal = draft.goals[event.goalId];
      if (goal) {
        draft.goals[event.goalId] = { ...goal, status: event.status };
        if (event.status === 'completed') draft.totals.goalsCompleted += 1;
      }
      break;
    }

    // ── activity ─────────────────────────────────────────────────────────
    case 'habit.completed': {
      const key = completionKey(event.habitId, event.studyDate);
      const level = event.level ?? 'full';
      const previous = draft.completions[key];

      // The generic award above was added at the FULL rate, because it keys
      // off the event type alone and cannot see the level. Undo it here and
      // pay what this level actually earns.
      if (flat) day.xpFlat -= flat;

      if (!previous) {
        draft.completions[key] = level;
        day.habitsCompleted += 1;
        if (level === 'floor') day.floorsKept += 1;
        draft.totals.habitsCompleted += 1;
        day.xpFlat += habitAward(ruleset, level);
        // Remember WHEN, so the anchor can later be proposed rather than
        // asked for. Only on a genuinely new completion: re-ticking or
        // upgrading a floor is not a second performance of the habit, and
        // counting it would bias the rhythm toward whenever someone last
        // fiddled with a row.
        const habit = draft.habits[event.habitId];
        if (habit) {
          const hours = [...habit.recentHours, localHour(event.ts, event.tzOffsetMinutes)];
          draft.habits[event.habitId] = {
            ...habit,
            recentHours: hours.slice(-RHYTHM_SAMPLES),
          };
        }
      } else if (previous !== level) {
        // Upgrading floor → full (finished it after all), or stepping back.
        // The promise was already counted as kept; only the award moves.
        draft.completions[key] = level;
        day.floorsKept = Math.max(0, day.floorsKept + (level === 'floor' ? 1 : -1));
        day.xpFlat += habitAward(ruleset, level) - habitAward(ruleset, previous);
      }
      // Same level twice earns nothing, and the generic award is already undone.
      break;
    }
    case 'habit.uncompleted': {
      const key = completionKey(event.habitId, event.studyDate);
      const previous = draft.completions[key];
      if (previous) {
        delete draft.completions[key];
        day.habitsCompleted = Math.max(0, day.habitsCompleted - 1);
        if (previous === 'floor') day.floorsKept = Math.max(0, day.floorsKept - 1);
        draft.totals.habitsCompleted = Math.max(0, draft.totals.habitsCompleted - 1);
        day.xpFlat = Math.max(0, day.xpFlat - habitAward(ruleset, previous));
      }
      break;
    }

    case 'task.completed': {
      const task = draft.tasks[event.taskId];
      if (task && !task.done) {
        draft.tasks[event.taskId] = { ...task, done: true, completedOn: event.studyDate };
        day.tasksCompleted += 1;
        draft.totals.tasksCompleted += 1;
      } else if (flat) {
        day.xpFlat -= flat;
      }
      break;
    }
    case 'task.uncompleted': {
      const task = draft.tasks[event.taskId];
      if (task?.done) {
        // The key is dropped, not set to undefined: `completedOn` absent is
        // the only representation of "not done" the type allows.
        const { completedOn: _wasDoneOn, ...rest } = task;
        draft.tasks[event.taskId] = { ...rest, done: false };
        day.tasksCompleted = Math.max(0, day.tasksCompleted - 1);
        draft.totals.tasksCompleted = Math.max(0, draft.totals.tasksCompleted - 1);
        const award = ruleset.xp.flat['task.completed'] ?? 0;
        day.xpFlat = Math.max(0, day.xpFlat - award);
      }
      break;
    }

    // ── entries ──────────────────────────────────────────────────────────
    // One case for both capture routes. What used to be two branches that
    // disagreed — one creating a listable record, the other only adding
    // minutes — is now impossible to get wrong, because there is only one.
    case 'entry.logged': {
      const timed = event.mode === 'timed';
      draft.entries[event.entryId] = {
        id: event.entryId,
        track: event.track,
        minutes: event.minutes,
        kind: event.kind,
        mode: event.mode,
        date: event.studyDate,
        deleted: false,
        ...(event.planMinutes === undefined ? {} : { planMinutes: event.planMinutes }),
        ...(event.timerMode === undefined ? {} : { timerMode: event.timerMode }),
        ...(event.intention === undefined ? {} : { intention: event.intention }),
        ...(event.nextStep === undefined ? {} : { nextStep: event.nextStep }),
        ...(event.recall === undefined ? {} : { recall: event.recall }),
      };
      day.minutes += event.minutes;
      draft.totals.minutes += event.minutes;
      if (event.kind === 'review') {
        day.reviewMinutes += event.minutes;
        draft.totals.reviewMinutes += event.minutes;
      }
      if (timed) {
        day.timedSessions += 1;
        draft.totals.timedSessions += 1;
        if (event.timerMode === 'pomodoro') day.pomodoroSessions += 1;
        if (event.minutes >= 90) day.longSessions += 1;
      }
      addTrack(draft, event.track, event.minutes, timed ? 1 : 0);
      recomputeMinuteXp(day, ruleset);
      break;
    }

    case 'entry.corrected': {
      const entry = draft.entries[event.entryId];
      if (!entry || entry.deleted) break;
      const target = touchDay(draft, entry.date);
      const kind = event.kind ?? entry.kind;
      const track = event.track ?? entry.track;

      // Unwind the old figures, then re-apply the new ones. Cheaper to reason
      // about than a pile of deltas, and it handles a kind or track flip —
      // which the old study.corrected could not do at all — for free.
      target.minutes = Math.max(0, target.minutes - entry.minutes + event.minutes);
      draft.totals.minutes = Math.max(0, draft.totals.minutes - entry.minutes + event.minutes);
      if (entry.kind === 'review') {
        target.reviewMinutes = Math.max(0, target.reviewMinutes - entry.minutes);
        draft.totals.reviewMinutes = Math.max(0, draft.totals.reviewMinutes - entry.minutes);
      }
      if (kind === 'review') {
        target.reviewMinutes += event.minutes;
        draft.totals.reviewMinutes += event.minutes;
      }
      addTrack(draft, entry.track, -entry.minutes, 0);
      addTrack(draft, track, event.minutes, 0);

      draft.entries[event.entryId] = { ...entry, minutes: event.minutes, kind, track };
      recomputeMinuteXp(target, ruleset);
      break;
    }

    case 'entry.deleted': {
      const entry = draft.entries[event.entryId];
      if (!entry || entry.deleted) break;
      const target = touchDay(draft, entry.date);
      target.minutes = Math.max(0, target.minutes - entry.minutes);
      draft.totals.minutes = Math.max(0, draft.totals.minutes - entry.minutes);
      if (entry.kind === 'review') {
        target.reviewMinutes = Math.max(0, target.reviewMinutes - entry.minutes);
        draft.totals.reviewMinutes = Math.max(0, draft.totals.reviewMinutes - entry.minutes);
      }
      if (entry.mode === 'timed') {
        target.timedSessions = Math.max(0, target.timedSessions - 1);
        draft.totals.timedSessions = Math.max(0, draft.totals.timedSessions - 1);
        if (entry.timerMode === 'pomodoro') {
          target.pomodoroSessions = Math.max(0, target.pomodoroSessions - 1);
        }
        if (entry.minutes >= 90) target.longSessions = Math.max(0, target.longSessions - 1);
        // The bonus was paid for committing to the block; deleting the entry
        // withdraws the claim, so it goes back.
        target.xpFlat = Math.max(0, target.xpFlat - ruleset.xp.timedSessionBonus);
      }
      addTrack(draft, entry.track, -entry.minutes, entry.mode === 'timed' ? -1 : 0);
      draft.entries[event.entryId] = { ...entry, deleted: true };
      recomputeMinuteXp(target, ruleset);
      break;
    }

    case 'session.abandoned':
      // Recorded for honesty in history, but produces no entry, earns nothing
      // and is not "activity" — a bailed session must never build a streak.
      break;

    case 'measure.defined':
      draft.measures[event.measureId] = {
        id: event.measureId,
        name: event.name,
        shape: event.shape,
        ...(event.track === undefined ? {} : { track: event.track }),
        ...(event.outOf === undefined ? {} : { outOf: event.outOf }),
        ...(event.unit === undefined ? {} : { unit: event.unit }),
      };
      break;
    case 'reading.recorded':
      draft.readings[event.readingId] = {
        id: event.readingId,
        measureId: event.measureId,
        value: event.value,
        date: event.studyDate,
        deleted: false,
        ...(event.entryId === undefined ? {} : { entryId: event.entryId }),
        ...(event.outOf === undefined ? {} : { outOf: event.outOf }),
      };
      break;
    case 'reading.corrected': {
      const reading = draft.readings[event.readingId];
      if (reading && !reading.deleted) {
        draft.readings[event.readingId] = {
          ...reading,
          value: event.value,
          ...(event.outOf === undefined ? {} : { outOf: event.outOf }),
        };
      }
      break;
    }
    case 'reading.deleted': {
      const reading = draft.readings[event.readingId];
      if (reading) draft.readings[event.readingId] = { ...reading, deleted: true };
      break;
    }

    // ── materialised facts ───────────────────────────────────────────────
    case 'achievement.unlocked':
      draft.achievements[event.achievementId] ??= { unlockedAt: event.ts };
      break;
    case 'rank.reached':
      draft.ranksReached[event.rankId] ??= event.ts;
      break;
    case 'profile.named':
      draft.profileName = event.name;
      break;

    case 'profile.pathSet':
      draft.profilePath = event.path;
      break;
  }
}

function expectedHabitsOn(habits: Habit[], date: StudyDate): number {
  let expected = 0;
  for (const habit of habits) expected += expectedWeight(habit, date);
  return expected;
}

/**
 * Second pass, over days rather than events.
 *
 * Streaks, streak bonuses and Titles all need a global view, and doing them
 * incrementally breaks the moment a merged log delivers an out-of-order day.
 * This is O(days) — ~365 keys a year — so it stays cheap even at full replay.
 */
function finalise(draft: DerivedState, ruleset: Ruleset): void {
  const dates = Object.keys(draft.days).sort();

  let current = 0;
  let longest = 0;
  let previousIndex: number | null = null;
  let lastActive: StudyDate | null = null;
  let gapBeforeStreak = 0;
  let power = 0;

  for (const date of dates) {
    const day = draft.days[date] as DayStats;

    // Reset before deciding: this pass re-runs over days that already carry
    // values from an earlier fold, and a day can stop being a comeback when a
    // late-arriving event from another device fills the gap behind it.
    day.comeback = false;
    day.xpComeback = 0;
    day.missedBefore = 0;

    if (day.active) {
      const index = dayIndex(date);
      if (previousIndex === null) {
        current = 1;
        gapBeforeStreak = 0;
      } else if (index - previousIndex === 1) {
        current += 1;
      } else {
        gapBeforeStreak = index - previousIndex - 1;
        current = 1;
        // Active, and the day before was not. This is the return — the single
        // highest-value behaviour in the literature (§43), and the reason the
        // product exists in the shape it does.
        //
        // The first active day ever is NOT a comeback: `previousIndex === null`
        // means there was nothing to come back from.
        day.comeback = true;
        day.xpComeback = ruleset.xp.comebackBonus;
        day.missedBefore = gapBeforeStreak;
      }
      previousIndex = index;
      lastActive = date;
      longest = Math.max(longest, current);
      day.xpStreak = ruleset.xp.streakBonus(current);
    } else {
      day.xpStreak = 0;
    }

    power += day.xpFlat + day.xpMinutes + day.xpStreak + day.xpComeback;
  }

  draft.power = Math.max(0, Math.round(power));
  draft.streak = { current, longest, lastActiveDate: lastActive };

  const progress = rankProgress(draft.power, ruleset);
  draft.rank = progress.current.id;
  draft.rankRatio = progress.ratio;

  const window = behaviourWindow(draft, lastActive, gapBeforeStreak);
  draft.titles = ruleset.titles
    .filter((rule) => rule.test(window))
    .sort((a, b) => b.priority - a.priority)
    .slice(0, ruleset.maxTitles)
    .map((rule) => rule.id as TitleId);
}

export function behaviourWindow(
  state: DerivedState,
  endDate: StudyDate | null,
  gapBeforeStreak = 0,
): BehaviourWindow {
  const window: BehaviourWindow = {
    windowDays: TITLE_WINDOW_DAYS,
    activeDays: 0,
    currentStreak: state.streak.current,
    habitsCompleted: 0,
    habitsExpected: 0,
    tasksCompleted: 0,
    tasksOpened: 0,
    minutes: 0,
    reviewMinutes: 0,
    timedSessions: 0,
    pomodoroSessions: 0,
    longSessions: 0,
    lateActivities: 0,
    earlyActivities: 0,
    activities: 0,
    gapBeforeStreak,
  };
  if (!endDate) return window;

  const habits = Object.values(state.habits);
  const end = dayIndex(endDate);

  for (const [date, day] of Object.entries(state.days)) {
    const index = dayIndex(date);
    if (index > end || index <= end - TITLE_WINDOW_DAYS) continue;
    if (day.active) window.activeDays += 1;
    window.habitsCompleted += day.habitsCompleted;
    window.tasksCompleted += day.tasksCompleted;
    window.tasksOpened += day.tasksOpened;
    window.minutes += day.minutes;
    window.reviewMinutes += day.reviewMinutes;
    window.timedSessions += day.timedSessions;
    window.pomodoroSessions += day.pomodoroSessions;
    window.longSessions += day.longSessions;
    window.lateActivities += day.lateActivities;
    window.earlyActivities += day.earlyActivities;
    window.activities += day.activities;
    window.habitsExpected += expectedHabitsOn(habits, date);
  }

  return window;
}

/**
 * Incremental application is only valid when every incoming event sorts after
 * the checkpoint. A merged log from another device can deliver older ids, and
 * folding those onto an existing state would double-count. Callers check this
 * and fall back to `replay`.
 */
export function canApplyIncrementally(
  state: DerivedState,
  events: readonly AppEvent[],
  ruleset: Ruleset,
): boolean {
  if (state.rulesetVersion !== ruleset.version) return false;
  if (state.cursor === null) return true;
  return events.every((event) => event.id > (state.cursor as string));
}

/** Fold new events onto an existing checkpoint. Events must be sorted. */
export function reduce(
  state: DerivedState,
  events: readonly AppEvent[],
  ruleset: Ruleset,
): DerivedState {
  const draft = cloneState(state);
  for (const event of events) {
    if (draft.cursor !== null && event.id <= draft.cursor) continue;
    applyEvent(draft, event, ruleset);
    draft.cursor = event.id;
    draft.eventCount += 1;
  }
  finalise(draft, ruleset);
  return draft;
}

/** Rebuild from nothing. Used on ruleset version bump, checksum failure, or
 *  an out-of-order merge. */
export function replay(events: readonly AppEvent[], ruleset: Ruleset): DerivedState {
  return reduce(initialState(ruleset.version), events, ruleset);
}

/**
 * Achievements to emit, given current state.
 *
 * Core only *detects*; the caller appends `achievement.unlocked` events so
 * the award becomes a permanent fact in the log rather than something a
 * future ruleset could silently revoke.
 */
export function detectUnlocks(state: DerivedState, ruleset: Ruleset): string[] {
  const context = {
    totalMinutes: state.totals.minutes,
    totalTimedSessions: state.totals.timedSessions,
    currentStreak: state.streak.current,
    longestStreak: state.streak.longest,
    goalsStarted: state.totals.goalsStarted,
    goalsCompleted: state.totals.goalsCompleted,
    window: behaviourWindow(state, state.streak.lastActiveDate),
  };
  return ruleset.achievements
    .filter((rule) => !state.achievements[rule.id] && rule.test(context))
    .map((rule) => rule.id);
}

/**
 * Whether the streak is still alive as of `today`.
 *
 * Core has no clock, so `state.streak.current` is the streak *as of the last
 * active day*. Only the caller knows what today is.
 */
export function isStreakAlive(state: DerivedState, today: StudyDate): boolean {
  const last = state.streak.lastActiveDate;
  if (!last) return false;
  return dayIndex(today) - dayIndex(last) <= 1;
}
